import {
    ProductsServicesOrderDetails,
    SelectedServiceProduct,
    SelectedService,
    SelectedProduct,
    ShoppingCartData,
    ServiceType,
    Service,
    Product,
    SelectedLocation,
    Category,
    ServiceAddress} from './ProductsServicesModels';
import { DateTime } from 'luxon';
import {cartData, getCartById} from '@/products-and-services/CartServices';

interface CustomerSelections {
    cartNumber: string;
    appUserId: number;
    createdDate: DateTime;
    orderDetails: ProductsServicesOrderDetails;
}

interface State {
    productsServicesOrderDetails: ProductsServicesOrderDetails;
    customerSelections: CustomerSelections[];
    shoppingCartData: ShoppingCartData;
    storedItem: SelectedServiceProduct | null;
    states: State[];
}

export default {
    namespaced: true,
    state: {
        productsServicesOrderDetails: defaultDetails(),
        shoppingCartData: {} as ShoppingCartData,
        storedItem: null,
        customerSelections: [],
        states: []
    },
    mutations: {
        updateCartServiceLocation(state: State, location: SelectedLocation) {
            const orderDetails = state.productsServicesOrderDetails;
            // if new location reset services
            if (orderDetails.serviceAddress !== location.serviceAddress
                || orderDetails.serviceUnit !== location.serviceUnit
                || orderDetails.serviceCity !== location.serviceCity) {
                orderDetails.services = [];
            }
            state.productsServicesOrderDetails = {...orderDetails, ...(location || {})};
        },
        addToCart(state: State, {serviceId, serviceCatId, productId, productCatId, value}: SelectedServiceProduct) {
            const services = [...(state.productsServicesOrderDetails.services || [])];
            if (services.some(svc => isEqual(svc.serviceId, serviceId) && isEqual(svc.catId, serviceCatId))) {
                state.productsServicesOrderDetails.services = services.map(subSvc => {
                    if (isEqual(subSvc.serviceId, serviceId) && isEqual(subSvc.catId, serviceCatId) && productCatId && productId) {
                        const products = Array.from(new Set([...subSvc.products, {productId, catId: productCatId, value}]));
                        return {...subSvc, products};
                    }
                    return subSvc;
                });
            } else {
                if (serviceId && serviceCatId && productId && productCatId) {
                    state.productsServicesOrderDetails.services =
                        [...services, {serviceId, catId: serviceCatId, products: [{productId, catId: productCatId, value}]}];
                }
                else  if (serviceId && serviceCatId) {
                    state.productsServicesOrderDetails.services =
                        [...services, {serviceId, catId: serviceCatId, products: []}];
                } else {
                    state.productsServicesOrderDetails.services = [...services];
                }
            }
        },
        removeFromCart(state: State, item: SelectedServiceProduct) {
            const services = [...(state.productsServicesOrderDetails.services || [])];
            state.productsServicesOrderDetails.services = services.reduce((acc: SelectedService[], svc: SelectedService) => {
                if (isEqual(svc.serviceId, item.serviceId) && isEqual(svc.catId, item.serviceCatId)) {
                    if (!(item.productId && item.productCatId)) {
                        // remove the service
                        return acc;
                    }
                    const products = svc.products
                        .filter((prod: SelectedProduct) => !isEqual(prod.productId, item.productId) || !isEqual(prod.catId, item.productCatId));
                    // return filtered products
                    return [...acc, {...svc, products}];
                }
                return [...acc, svc];
            }, []);
        },
        clearCart(state: State) {
            state.productsServicesOrderDetails = defaultDetails();
        },
        cancelEmployeeEdits(state: State, appUserId: number): void {
            const cartNumber = state.shoppingCartData.id;
            const customerSelection = state.customerSelections
            .find(selection => selection.cartNumber === cartNumber && selection.appUserId === appUserId);
            if (customerSelection && customerSelection.orderDetails) {
                state.productsServicesOrderDetails = customerSelection.orderDetails;
            }
        },
        saveUserSelections(state: State, appUserId: number): void {
            const createdDate = DateTime.local();
            const weekAgo = createdDate.minus({week: 7});
            // remove anything over a week
            const customerSelections = state.customerSelections.filter(selection => selection.createdDate >= weekAgo);
            const cartNumber = state.shoppingCartData.id;
            const orderDetails = state.productsServicesOrderDetails;
            const customerSelection = state.customerSelections
            .find(selection => selection.cartNumber === cartNumber && selection.appUserId === appUserId);
            if (!(customerSelection && customerSelection.orderDetails)) {
                // add new selection
                state.customerSelections = [
                    ...customerSelections,
                    {
                        appUserId,
                        cartNumber,
                        orderDetails,
                        createdDate
                    }
                ];
            } else {
                // update token
                customerSelection.orderDetails.token = orderDetails.token;
            }
        },
        setShoppingCart(state: State, cartData: ShoppingCartData) {
            let services = [] as SelectedService[];
            state.shoppingCartData = cartData;
            if (cartData.selectedOptions && cartData.selectedOptions.services
                && cartData.selectedOptions.services.length) {
                    services = cartData.selectedOptions.services
                    .map(({serviceId, catId: serviceCatId, products}) =>
                        ({
                            serviceId,
                            catId: serviceCatId || '',
                            products: (products || [])
                            .map(({productId, catId: productCatId, value}) => ({
                                productId,
                                catId: productCatId || '',
                                value
                            }))
                        }));
            }
            const serviceCity = cartData.serviceCity ? cartData.serviceCity : null;
            const serviceUnit = cartData.serviceUnit ? cartData.serviceUnit : null;
            state.productsServicesOrderDetails = {
                ...state.productsServicesOrderDetails,
                location: cartData.serviceAddress,
                serviceAddress: cartData.serviceAddress,
                serviceUnit,
                serviceCity,
                services,
                token: cartData.token
            };
        },
        storeItem(state: State, item: SelectedServiceProduct) {
            state.storedItem = item;
        },
        clearStoredItem(state: State) {
            state.storedItem = null;
        },
    },
    getters: {
        isInCart: (state: State, {getShoppingCartSelection}: any) => (item: SelectedServiceProduct): boolean|undefined => {
            const cartSelection = getShoppingCartSelection();
            if (cartSelection && cartSelection.services) {
                if (item.productCatId && item.productId) {
                    return cartSelection.services.some(({serviceId, catId: serviceCatId, products}: Service) => {
                        if (!products) { return false; };
                        if (!isEqual(serviceId, item.serviceId) || (serviceCatId && !isEqual(serviceCatId, item.serviceCatId))) { return false}
                        return products.some(({productId, catId: productCatId}: Product) => isEqual(productId, item.productId)
                            && (productCatId && isEqual(productCatId, item.productCatId)));
                    });
                }

                // checking if a service is selected, no products value was passed in
                return cartSelection.services.some(({serviceId, catId: serviceCatId}: Service) =>
                    isEqual(serviceId, item.serviceId) && serviceCatId && isEqual(serviceCatId, item.serviceCatId));
            }
        },
        getCartNumber: (state: State) => (): string|number|undefined => {
            return state.shoppingCartData.id;
        },
        getShoppingCartSelection: (state: State) => ():any|undefined => {
            const shoppingCart = state.shoppingCartData;
            if (shoppingCart) {
                if (shoppingCart.selectedOptions && shoppingCart.selectedOptions.cartTotal) {
                    if (shoppingCart.selectedOptions.services) {
                        // convert main ids to strings
                        const services = shoppingCart.selectedOptions.services
                        .map(({products, ...service}) => ({
                            ...service,
                            serviceCatId: (service.catId || ''),
                            products: (products || []).map(({typeId, ...product}) => ({
                                ...product,
                                typeId: typeId ? Number(typeId) : -1,
                                productCatId: (product.catId || '')
                            }))
                        }));
                        return {...shoppingCart.selectedOptions, services, token: shoppingCart.token};
                    }
                    return shoppingCart.selectedOptions;
                } else {
                    return {};
                }
            }
        },
        getLocation: (state: State) => (): ServiceAddress|undefined => {
            const shoppingCart = state.shoppingCartData;
            if (shoppingCart) {
              if (shoppingCart.serviceAddress){
                return {
                  serviceAddress: shoppingCart.serviceAddress,
                  serviceUnit: shoppingCart.serviceUnit,
                  serviceCity: shoppingCart.serviceCity,
                  serviceCityDisplayName: shoppingCart.serviceCityDisplayName,
                  serviceState: shoppingCart.serviceState,
                  serviceZipCode: shoppingCart.serviceZipCode
                } as ServiceAddress;
              }
            }
        },
        getServiceTypeById: (state: State, {getAvailableServices}: any) => (serviceTypeId: string): ServiceType | undefined => {
            const serviceTypes = getAvailableServices() as ServiceType[];
            if (serviceTypes) {
                const serviceType = serviceTypes.find(({id}: ServiceType) => isEqual(id, serviceTypeId)) as ServiceType;
                if (serviceType && serviceType.services && serviceType.services.length) {
                    return {...serviceType, services: serviceType.services
                            .sort(({serviceId: a}: Service, {serviceId: b}: Service) => Number(a) - Number(b))};
                }
                return serviceType;
            }
        },
        getServiceByTypeIdAndId: (state: State, {getServiceTypeById}: any) => (serviceTypeId: string, serviceId: string): Service | undefined => {
            const serviceType = getServiceTypeById(serviceTypeId)  as ServiceType;
            if (serviceType && serviceType.services && serviceType.services.length) {
                const service = serviceType.services.find((svc: Service) => isEqual(svc.serviceId, serviceId));
                if (service && service.categories && service.categories.length) {
                    return {...service, categories: service.categories
                            .sort(({id: a}: Category, {id: b}: Category) => Number(a) - Number(b))};
                }
                return service;
            }
        },
        getAvailableServices: (state: State) => (): ServiceType[] | undefined => {
            const shoppingCart = state.shoppingCartData;
            if (shoppingCart && shoppingCart.availableOptions && shoppingCart.availableOptions.serviceTypes) {
                // convert main IDs to string
                return shoppingCart.availableOptions.serviceTypes
                .map(({services, ...serviceType}) => ({
                    ...serviceType,
                    serviceTypeId: serviceType.id,
                    services: (services || []).map(({categories, ...service}) => ({
                        ...service,
                        serviceCatId: (service.catId || ''),
                        categories: (categories || []).map(({products, ...category}) => ({
                            ...category,
                            products: (products || []).map(({typeId, ...product}) => ({
                                ...product,
                                typeId: typeId ? Number(typeId) : -1,
                                productCatId: (product.catId || '')
                            }))
                        }))
                    }))
                }));
            }
        },
        getStoredItem: (state: State) => (): SelectedServiceProduct|null => {
            return state.storedItem;
        },
    },
    actions: {
        async loadShoppingCart({ commit, state }: any) {
            commit('setShoppingCart', await cartData(state.productsServicesOrderDetails, state.shoppingCartData.id));
        },
        async loadShoppingCartById({ commit, state }: any, {cartNumber}: any) {
            commit('setShoppingCart', await getCartById(cartNumber));
        },
        async updateOrderDetails({ commit, dispatch }: any, params: {mutation: string, item: SelectedServiceProduct | SelectedLocation}) {
            if (params.item) {
                commit(params.mutation, params.item);
            } else {
                commit(params.mutation);
            }

            // don't fetch for new cart data from api
            if (params.item && params.item.noRefetch) {
                return;
            }
            await dispatch('loadShoppingCart');
        }
    }
};

function isEqual(val1: number|string|undefined, val2: number|string|undefined): boolean {
    if (val1 === val2) {
        return true;
    }
    return Boolean(val1 && val2 && val1.toString() === val2.toString());
}

function defaultDetails(): ProductsServicesOrderDetails {
    return {
        "location": "",
        "serviceAddress": "",
        "serviceCity": "",
        "serviceUnit": "",
        "services": [],
        "token": ""
    };
}
